import { ECONOMY } from '../constants/economy-registry';
import { rampLengthFor, trackPositionFor } from './track-map';
/** Seeded and default base: every first clear pays at least this, before the slope. */
export const REWARD_CURVE_BASE = ECONOMY.rewards.levelComplete.base;
/** Seeded and default slope: the user's 0.15 (chosen over 0.1 and 0.2, 2026-10-06), ×10 by Module 97. */
export const REWARD_CURVE_PER_LEVEL = ECONOMY.rewards.levelComplete.perLevel;
/** The server's accepted range for one level-complete credit (migration 0022). */
export const REWARD_CURVE_MIN = ECONOMY.rewards.levelComplete.min;
export const REWARD_CURVE_MAX = ECONOMY.rewards.levelComplete.max;
/** Module 97 (user 2026-10-07) — every reward is a multiple of this, rounded down. */
export const REWARD_STEP = ECONOMY.rewards.levelComplete.step;
/**
 * Module 97 (user 2026-10-07) — endless pay grows by one {@link REWARD_STEP}
 * every this many puzzles. Chosen over 6 and 10 (10 = the earlier "a coin
 * every two puzzles", which in steps of 5 repeats one amount ten times); 4
 * matches the ramp, where 1.5 a level steps every 3–4 puzzles.
 */
export const REWARD_ENDLESS_PUZZLES_PER_STEP = ECONOMY.rewards.levelComplete.endlessPuzzlesPerStep;
/** Coins for a first clear at `effectiveLevel`: down to a multiple of {@link REWARD_STEP}, clamped. */
export function coinsForLevel(effectiveLevel, base = REWARD_CURVE_BASE, perLevel = REWARD_CURVE_PER_LEVEL) {
    // The epsilon keeps a value that is a multiple of 5 in exact arithmetic but
    // lands a hair under it in floating point (a tuned slope like 0.15) on it.
    const raw = REWARD_STEP * Math.floor((base + perLevel * effectiveLevel) / REWARD_STEP + 1e-9);
    if (!Number.isFinite(raw))
        return REWARD_CURVE_MIN;
    return Math.min(REWARD_CURVE_MAX, Math.max(REWARD_CURVE_MIN, raw));
}
/**
 * Module 97 — coins for a first clear of a track's `ordinal`th puzzle.
 *
 * On the ramp the effective level rises one per puzzle, so this is
 * `coinsForLevel` of it. In the endless phase it is the ramp's last reward plus
 * `5 × ceil(n / 4)` for endless puzzle n — never the level of the board on
 * screen, which cycles down and back up. Clamped like `coinsForLevel`.
 */
export function coinsForTrackPuzzle(config, track, ordinal, base = REWARD_CURVE_BASE, perLevel = REWARD_CURVE_PER_LEVEL) {
    const ramp = rampLengthFor(config, track);
    const k = Math.max(1, Math.floor(Number.isFinite(ordinal) ? ordinal : 1));
    const rampTop = coinsForLevel(trackPositionFor(config, track, Math.min(k, ramp)).effectiveLevel, base, perLevel);
    if (k <= ramp)
        return rampTop;
    const bonus = REWARD_STEP * Math.ceil((k - ramp) / REWARD_ENDLESS_PUZZLES_PER_STEP);
    return Math.min(REWARD_CURVE_MAX, rampTop + bonus);
}
